import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, ExternalLink, ShieldCheck, Eye, Files, FileUp, Search, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from '../../components/admin/DataTable';
import AccountForm from '../../components/admin/AccountForm';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import StatusBadge from '../../components/admin/StatusBadge';
import PagesManager from '../../components/admin/PagesManager';
import UploadAuditModal from '../../components/admin/UploadAuditModal';
import FilterChip from '../../components/crm/FilterChip';
import PaginatedTable from '../../components/admin/PaginatedTable';
import usePaginatedQuery from '../../hooks/usePaginatedQuery';
import { api } from '../../lib/api';
import { SCHOOL_STAGES, stageMeta } from '../../lib/stages';

const BOROUGHS = [
  ['M', 'Manhattan'],
  ['X', 'Bronx'],
  ['K', 'Brooklyn'],
  ['Q', 'Queens'],
  ['R', 'Staten Island'],
];

const TYPES = [
  ['', 'All'],
  ['school', 'School'],
  ['external', 'External'],
];

const FILTER_KEYS = ['lifecycle_stage', 'type', 'district', 'borough', 'q', 'stage_view', 'owner'];

// Quick-filter chips at the top of the unified accounts list. Item 1.2 of the
// comprehensive plan. Default view "In Pipeline" surfaces accounts in active
// sales stages so admin lands on the work-in-progress when /admin/accounts
// loads with no other params. The "all" key is explicit (not empty string) so
// "user chose All" is distinguishable from "no params yet, fall back to default".
const STAGE_VIEWS = [
  { key: 'all',        label: 'All',         stages: null },
  { key: 'pipeline',   label: 'In Pipeline', stages: ['contacted', 'engaged', 'verbal_yes', 'contracted'] },
  { key: 'prospect',   label: 'Prospects',   stages: ['prospect'] },
  { key: 'maintained', label: 'Maintained',  stages: ['maintained'] },
  { key: 'dormant',    label: 'Dormant',     stages: ['dormant'] },
  { key: 'closed',     label: 'Closed',      stages: ['closed_lost'] },
];

// Default landing view. "All" lands users on the full account roster so the
// page populates on first visit even when nothing is in the pre-contract
// pipeline yet. Click "In Pipeline" to focus on active sales work.
const DEFAULT_STAGE_VIEW_KEY = 'all';
const STAGE_VIEW_BY_KEY = Object.fromEntries(STAGE_VIEWS.map((v) => [v.key, v]));

export default function Accounts() {
  const navigate = useNavigate();
  const [impersonating, setImpersonating] = useState(null);

  // Sprint J: clicking the Eye icon mints a real client session for the
  // account's primary user and lands the admin in /portal/* as them. The
  // banner across the top says "Previewing as ..." with an End button.
  const startImpersonation = useCallback(async (accountId) => {
    if (impersonating) return;
    setImpersonating(accountId);
    try {
      const res = await api.post(`/api/admin/impersonate/${accountId}`);
      window.location.href = res?.redirect || '/portal/overview';
    } catch (err) {
      const code = err?.data?.code;
      if (code === 'no_client_user') {
        window.alert('This account has no active client user to preview as. Create a principal user first.');
      } else {
        window.alert(err?.data?.error || err?.message || 'Failed to start preview');
      }
      setImpersonating(null);
    }
  }, [impersonating]);
  const [searchParams, setSearchParams] = useSearchParams();

  const lifecycleStage = searchParams.get('lifecycle_stage') || '';
  const type = searchParams.get('type') || '';
  const district = searchParams.get('district') || '';
  const borough = searchParams.get('borough') || '';
  const q = searchParams.get('q') || '';
  const ownerFilter = searchParams.get('owner') || '';

  // Stage-view selector. Defaults to "All" when no params are present so the
  // page populates with the full account roster on first visit. Click
  // "In Pipeline" to focus on pre-contract active sales stages.
  const explicitStageParams = searchParams.has('stage_view') || searchParams.has('lifecycle_stage');
  const stageView = explicitStageParams
    ? (searchParams.get('stage_view') || 'all')  // ?lifecycle_stage=foo with no stage_view treats stageView as 'all' (per-stage chip below applies)
    : DEFAULT_STAGE_VIEW_KEY;
  const stageViewDef = STAGE_VIEW_BY_KEY[stageView] || STAGE_VIEW_BY_KEY.all;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pagesAccount, setPagesAccount] = useState(null);
  const [auditAccount, setAuditAccount] = useState(null);

  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    if (qInput === q) return undefined;
    const t = setTimeout(() => updateParams({ q: qInput || null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const [districtInput, setDistrictInput] = useState(district);
  useEffect(() => { setDistrictInput(district); }, [district]);
  useEffect(() => {
    if (districtInput === district) return undefined;
    const t = setTimeout(() => updateParams({ district: districtInput || null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtInput]);

  const updateParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      // Filter changes always reset offset; the user expects to land on page 1
      // after toggling a chip, not stay on a non-existent page 8 of a smaller set.
      next.delete('offset');
      return next;
    });
  }, [setSearchParams]);

  const anyFilterSet = useMemo(
    () => FILTER_KEYS.some((k) => (searchParams.get(k) || '').length > 0),
    [searchParams],
  );

  // The API supports a single `lifecycle_stage` param. The stage-view chips
  // map to either a single stage (e.g. "Prospects" → 'prospect') or a set
  // (e.g. "In Pipeline" → 4 stages). For multi-stage views we send
  // `lifecycle_stages` (plural, CSV) and the API returns the union; for
  // single-stage we keep `lifecycle_stage` for backward compat with deep-links.
  const stageParams = stageViewDef.stages
    ? (stageViewDef.stages.length === 1
        ? { lifecycle_stage: stageViewDef.stages[0], lifecycle_stages: '' }
        : { lifecycle_stage: '', lifecycle_stages: stageViewDef.stages.join(',') })
    : { lifecycle_stage: lifecycleStage || '', lifecycle_stages: '' };

  const paginator = usePaginatedQuery({
    path: '/api/admin/accounts',
    responseKey: 'accounts',
    defaultLimit: 100,
    extraParams: {
      ...stageParams,
      type,
      district,
      borough,
      q,
      owner: ownerFilter,
    },
  });

  const accounts = paginator.rows;
  const total = paginator.total;

  const onSaved = (account) => {
    paginator.refetch();
    return account;
  };

  const onDelete = async (account) => {
    await api.delete(`/api/admin/accounts/${account.id}`);
    paginator.refetch();
  };

  const clearFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const k of FILTER_KEYS) next.delete(k);
      next.delete('offset');
      return next;
    });
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <div className="text-white font-medium">{row.name}</div>
          <div className="text-[11px] text-gray-600 font-mono mt-0.5">{row.slug}</div>
        </div>
      ),
    },
    {
      key: 'siteUrl',
      header: 'Live site',
      render: (row) => {
        // "Editing": the school can use Edit text & images right now (the
        // effective switch — automatic or set on the account page).
        const editing = row.inlineEditEnabled === true && (
          <span title="Edit text & images is on for this school" data-testid={`editing-chip-${row.id}`}>
            <StatusBadge tone="success">Editing</StatusBadge>
          </span>
        );
        if (!row.siteUrl) {
          return (
            <span className="inline-flex items-center gap-2 flex-wrap">
              <span className="text-gray-600 text-[13px]">Not set</span>
              {editing}
            </span>
          );
        }
        // Some seeded URLs are malformed (missing protocol, embedded whitespace,
        // etc.). new URL() throws on those — without this try/catch a single
        // bad row crashes the whole accounts list render and we see a blank
        // page. Falling back to displaying the raw string is safer.
        let host = row.siteUrl;
        try { host = new URL(row.siteUrl).hostname; } catch { /* keep raw */ }
        return (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <a
              href={row.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] text-gray-300 hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {host}
              <ExternalLink className="w-3 h-3" />
            </a>
            {editing}
          </span>
        );
      },
    },
    {
      key: 'cfZoneId',
      header: 'Zone',
      render: (row) =>
        row.cfZoneId ? (
          <StatusBadge tone="success" icon={ShieldCheck}>Configured</StatusBadge>
        ) : (
          <StatusBadge tone="muted">Missing</StatusBadge>
        ),
    },
    {
      key: 'auditScore',
      header: 'WCAG',
      sortable: true,
      align: 'right',
      render: (row) =>
        row.auditScore != null ? (
          <span className="font-mono text-[13px]">
            <span className="text-white">{row.auditScore}</span>
            <span className="text-gray-600">/100</span>
          </span>
        ) : (
          <span className="text-gray-600">—</span>
        ),
    },
    {
      key: 'userCount',
      header: 'Users',
      sortable: true,
      align: 'right',
      render: (row) => (
        <span className="font-mono text-[13px] text-gray-300">{row.userCount}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => startImpersonation(row.id)}
            disabled={impersonating === row.id}
            className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/[0.05] transition-colors disabled:opacity-50"
            title="Sign in as principal (preview portal)">
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setAuditAccount(row)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/[0.05] transition-colors" title="Upload audit">
            <FileUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setPagesAccount(row)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/[0.08] transition-colors" title="Manage pages">
            <Files className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setEditing(row); setFormOpen(true); }}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors" title="Edit">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setConfirmDelete(row)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/[0.05] transition-colors" title="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Accounts</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Schools and districts using the NexSav client portal.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all"
        >
          <Plus className="w-4 h-4" />
          New account
        </button>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              aria-label="Search by name or DBN"
              className="pl-9 pr-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40 w-60"
              placeholder="Search name or DBN"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1" data-testid="type-segmented">
            {TYPES.map(([v, l]) => (
              <FilterChip key={v || 'all'} active={type === v}
                onClick={() => updateParams({ type: v || null })}>{l}</FilterChip>
            ))}
          </div>
          {(type === '' || type === 'school') && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="borough-chips">
              <FilterChip active={borough === ''} onClick={() => updateParams({ borough: null })}>All boroughs</FilterChip>
              {BOROUGHS.map(([v, l]) => (
                <FilterChip key={v} active={borough === v} onClick={() => updateParams({ borough: v })}>{l}</FilterChip>
              ))}
            </div>
          )}
          <input
            aria-label="Filter by district"
            className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40 w-24"
            placeholder="District"
            inputMode="numeric"
            value={districtInput}
            onChange={(e) => setDistrictInput(e.target.value.replace(/[^0-9]/g, ''))}
          />
          {anyFilterSet && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border bg-white/[0.02] border-white/[0.08] text-gray-400 hover:text-white hover:border-white/[0.15]"
              data-testid="clear-filters"
            >
              <X className="w-3 h-3" />
              Clear filters
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="stage-view-chips">
          {STAGE_VIEWS.map((v) => (
            <FilterChip
              key={v.key}
              active={stageView === v.key}
              onClick={() => updateParams({ stage_view: v.key, lifecycle_stage: null })}
            >
              {v.label}
            </FilterChip>
          ))}
          {/* "My accounts" filter — multi-owner UI ships in B.3 but the chip
              wires the URL today so the data layer is ready. */}
          <FilterChip
            active={ownerFilter === 'me'}
            onClick={() => updateParams({ owner: ownerFilter === 'me' ? null : 'me' })}
            data-testid="my-accounts-chip"
          >
            My accounts
          </FilterChip>
        </div>
        {/* Granular per-stage chips kept for power-users + URL deep-links. */}
        <div className="flex flex-wrap items-center gap-1 opacity-70" data-testid="stage-chips">
          <FilterChip active={lifecycleStage === '' && !stageView} onClick={() => updateParams({ lifecycle_stage: null, stage_view: null })}>All stages</FilterChip>
          {SCHOOL_STAGES.map((stage) => (
            <FilterChip key={stage} active={lifecycleStage === stage && !stageView}
              onClick={() => updateParams({ lifecycle_stage: stage, stage_view: null })}>{stageMeta(stage).label}</FilterChip>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-gray-500 mb-2" data-testid="accounts-count">
        {paginator.loading
          ? 'Loading…'
          : `Showing ${accounts.length} of ${total} ${total === 1 ? 'account' : 'accounts'}${anyFilterSet ? ' · filtered' : ''}`}
      </div>

      <PaginatedTable
        paginator={paginator}
        anyFilterSet={anyFilterSet}
        emptyMessage="No accounts yet."
        emptyFilteredMessage="No accounts match these filters."
        renderTable={(rows) => (
          <DataTable
            columns={columns}
            rows={rows}
            defaultSort={{ key: 'name', direction: 'asc' }}
            loading={false}
            emptyMessage=""
            onRowClick={(row) => navigate(`/admin/accounts/${row.id}`)}
          />
        )}
      />

      <AccountForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={onSaved} initial={editing} />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete account?"
        description={
          confirmDelete
            ? `This will permanently delete "${confirmDelete.name}" and all of its users, tickets, and edit requests. This can't be undone.`
            : ''
        }
        confirmLabel="Delete account"
        destructive
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => onDelete(confirmDelete)}
      />

      <PagesManager open={Boolean(pagesAccount)} onClose={() => setPagesAccount(null)} account={pagesAccount} />

      <UploadAuditModal open={Boolean(auditAccount)} onClose={() => setAuditAccount(null)} onSaved={onSaved} account={auditAccount} />
    </div>
  );
}
